import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Music, Play, Square } from "lucide-react";
import api, { API, apiError } from "@/lib/api";

const VOLUMES = [
  { v: "subtle", l: "Subtle" },
  { v: "medium", l: "Medium" },
  { v: "loud", l: "Loud" },
];

export default function SoundtrackCard({ videoId, musicTrack, musicVolume, onSaved }) {
  const [tracks, setTracks] = useState([]);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const track = musicTrack || "";
  const volume = musicVolume || "subtle";

  useEffect(() => {
    api.get("/music-tracks").then((r) => setTracks(r.data.tracks || [])).catch(() => {});
    return () => audioRef.current?.pause();
  }, []);

  const save = async (patch) => {
    try {
      await api.put(`/videos/${videoId}/music`, { track: track || null, volume, ...patch });
      onSaved?.();
      if (patch.track !== undefined) {
        toast.success(patch.track ? "Background music set — it'll be mixed in on your next recreate." : "Background music removed.");
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const togglePreview = () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    if (!track) return;
    audioRef.current?.pause();
    const a = new Audio(`${API}/music-tracks/${track}/preview`);
    a.volume = 0.6;
    a.play().catch(() => toast.error("Couldn't play the preview."));
    a.onended = () => setPlaying(false);
    audioRef.current = a;
    setPlaying(true);
  };

  const selected = tracks.find((t) => t.key === track);

  return (
    <div className="border border-white/10 rounded-md bg-[#121212] p-5 space-y-3" data-testid="soundtrack-panel">
      <div className="flex items-start gap-2">
        <Music className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-zinc-200">Background music <span className="text-zinc-500 font-normal">· optional</span></p>
          <p className="text-xs text-zinc-500 mt-0.5">Mixed softly under your narration when the video is created.</p>
        </div>
      </div>

      <div className="flex gap-2">
        <select
          value={track}
          onChange={(e) => {
            audioRef.current?.pause();
            setPlaying(false);
            save({ track: e.target.value || null });
          }}
          data-testid="music-track-select"
          className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
        >
          <option value="">No music</option>
          {tracks.map((t) => (
            <option key={t.key} value={t.key}>{t.name} — {t.mood}</option>
          ))}
        </select>
        <button
          onClick={togglePreview}
          disabled={!track}
          data-testid="music-preview-btn"
          title={playing ? "Stop preview" : "Preview track"}
          className="shrink-0 h-[42px] w-[42px] flex items-center justify-center rounded-sm border border-white/10 bg-[#0A0A0A] text-zinc-300 hover:text-white hover:bg-white/5 disabled:opacity-40 transition-colors"
        >
          {playing ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
      </div>

      {track && (
        <>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-1.5">Music volume</label>
            <div className="grid grid-cols-3 gap-2">
              {VOLUMES.map((o) => (
                <button
                  key={o.v}
                  onClick={() => save({ volume: o.v })}
                  data-testid={`music-volume-${o.v}`}
                  className={`py-2 rounded-sm text-xs font-semibold border transition-colors ${
                    volume === o.v
                      ? "bg-blue-600 border-blue-500 text-white"
                      : "bg-[#0A0A0A] border-white/10 text-zinc-400 hover:bg-white/5"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          {selected?.credit && (
            <p className="text-[11px] text-zinc-600 leading-relaxed" data-testid="music-credit-note">
              Free-to-use track. The credit line is added to your YouTube description automatically.
            </p>
          )}
        </>
      )}
    </div>
  );
}
